import type { LocalRow, SeedTables } from '../types/localDb.types'
import { seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Master data JAL WMS (ERD v010: mt_material_group, mt_unit, mt_material, mt_companies,
 * mt_material_pack_standard, mt_warehouses, mt_equipment) + saldo rm_unit.
 * Angka diambil dari seed prototype `jal-v7.43` (master.materials, master.suppliers,
 * master.mat_supplier_pack, rm.units berstatus In Stock).
 */

const CATEGORIES = [
  ['tembakau', 'Tembakau'],
  ['etiket', 'Etiket'],
  ['papir', 'Papir'],
  ['filter', 'Filter'],
  ['ctp', 'CTP'],
  ['foil', 'Foil'],
  ['inner', 'Inner'],
  ['opp', 'OPP'],
  ['teartape', 'Teartape'],
  ['kraft', 'Kraft'],
  ['lem', 'Lem']
] as const

/** [kode, nama, kind] — kind ERD: `PACK` (unit besar/kemasan) atau `SMALL` (unit kecil). */
const UNITS = [
  ['BOX', 'Box', 'PACK'],
  ['ROLL', 'Roll', 'PACK'],
  ['DOS', 'Dos', 'PACK'],
  ['SAK', 'Sak', 'PACK'],
  ['BUNDLE', 'Bundle', 'PACK'],
  ['PAIL', 'Pail', 'PACK'],
  ['RIM', 'Rim', 'PACK'],
  ['PAK', 'Pak', 'PACK'],
  ['PCS', 'pcs', 'SMALL'],
  ['M', 'm', 'SMALL'],
  ['BTG', 'btg', 'SMALL'],
  ['KG', 'kg', 'SMALL'],
  ['LEMBAR', 'lembar', 'SMALL']
] as const

type UnitCode = (typeof UNITS)[number][0]
type CategoryCode = (typeof CATEGORIES)[number][0]

/** [kode, nama, kategori, unit besar default, isi per unit besar, unit kecil, kapasitas/hari, varian]. */
const MATERIALS: [string, string, CategoryCode, UnitCode, number, UnitCode, number, string | null][] = [
  ['TSG-REG', 'TSG Rajang Reguler', 'tembakau', 'SAK', 30, 'KG', 8, null],
  ['TSG-MLD', 'TSG Rajang Mild', 'tembakau', 'SAK', 30, 'KG', 4, null],
  ['ETK-BOLD20', 'Etiket Bold 20 (PT)', 'etiket', 'BOX', 33000, 'PCS', 297000, 'PT'],
  ['ETK-BOLD12', 'Etiket Bold 12 (PR/PT)', 'etiket', 'BOX', 33000, 'PCS', 30000, 'PR/PT'],
  ['ETK-XTRA20', 'Etiket Xtra 20 (PT)', 'etiket', 'BOX', 33000, 'PCS', 198000, 'PT'],
  ['ETK-ONE12', 'Etiket Tabaco One 12', 'etiket', 'BOX', 33000, 'PCS', 20000, null],
  ['PPR-REG', 'Papir/Bobin Reguler Repse 27mm', 'papir', 'ROLL', 6000, 'M', 42000, null],
  ['PPR-MILD', 'Papir/Bobin Mild Repse 24mm', 'papir', 'ROLL', 6000, 'M', 12000, null],
  ['PPR-NAT', 'Papir Natural (SKT)', 'papir', 'RIM', 500, 'LEMBAR', 4000, null],
  ['FLT-B06', 'Filter RG 120-780-B06', 'filter', 'DOS', 4000, 'BTG', 24000, null],
  ['FLT-SF13', 'Filter RG 120-780-SF13', 'filter', 'DOS', 4000, 'BTG', 12000, null],
  ['CTP-BOLD', 'CTP Bold 5cm', 'ctp', 'ROLL', 3000, 'M', 3000, null],
  ['FOIL-BLK86', 'Foil Black 86mm', 'foil', 'ROLL', 2000, 'M', 6000, null],
  ['FOIL-GLD70', 'Foil Gold 70mm', 'foil', 'ROLL', 2000, 'M', 3000, null],
  ['INN-BLK96', 'Inner Black 96mm', 'inner', 'ROLL', 2000, 'M', 4000, null],
  ['INN-GLD79', 'Inner Gold 79mm', 'inner', 'ROLL', 2000, 'M', 3000, null],
  ['OPP-125', 'OPP Dalam Roll 125mm', 'opp', 'ROLL', 3000, 'M', 9000, null],
  ['OPP-PRS300', 'OPP Press 300x360 Semi Acrylic', 'opp', 'BOX', 2000, 'PCS', 3000, null],
  ['TT-GLDH', 'Teartape Gold Hologram 3mm', 'teartape', 'ROLL', 16000, 'M', 8000, null],
  ['KRF-75', 'Kraft Coklat 75x100cm', 'kraft', 'BUNDLE', 100, 'LEMBAR', 300, null],
  ['LEM-C', 'Lem CMC Food Grade', 'lem', 'PAIL', 25, 'KG', 50, null]
]

const SUPPLIERS = [
  ['SUP-KH', 'CV Karya Harapan', 'Kudus'],
  ['SUP-BMJ', 'PT Bukit Muria Jaya', 'Kudus'],
  ['SUP-IJ', 'PT Indra Jaya', 'Surabaya'],
  ['SUP-FLI', 'PT Filtrona Indonesia', 'Surabaya'],
  ['SUP-KTM', 'PT Kimia Perekat', 'Semarang'],
  ['SUP-TMG', 'KUD Tani Maju Temanggung', 'Temanggung'],
  ['SUP-MDR', 'UD Madura Tembakau Jaya', 'Pamekasan']
] as const

type SupplierCode = (typeof SUPPLIERS)[number][0]

const CUSTOMERS = [
  ['CUST-SRD', 'PT Sumber Rejeki Distribusindo', 'Semarang', 'Jl. Industri Raya 12, Semarang'],
  ['CUST-BN', 'CV Berkah Niaga', 'Surabaya', 'Jl. Kalianak Barat 88, Surabaya'],
  ['CUST-MKS', 'PT Mitra Kudus Sejahtera', 'Kudus', 'Jl. Sunan Kudus 45, Kudus'],
  ['DEPO-KDS', 'Depo Kudus', 'Kudus', 'Jl. Lingkar Utara Blok Pulo, Kudus'],
  ['CUST-EVAN', 'Bapak Evan', 'Surabaya', 'Perum Graha Family Blok T 38-39, Surabaya']
] as const

/** [material, supplier, unit besar, isi per unit besar, lead time hari] — pasangan material × supplier. */
const PACKS: [string, SupplierCode, UnitCode, number, number][] = [
  ['ETK-XTRA20', 'SUP-KH', 'BOX', 33000, 6],
  ['ETK-ONE12', 'SUP-KH', 'BOX', 33000, 6],
  ['ETK-BOLD12', 'SUP-KH', 'BOX', 33000, 5],
  ['INN-BLK96', 'SUP-KH', 'ROLL', 2000, 9],
  ['INN-GLD79', 'SUP-KH', 'ROLL', 2000, 9],
  ['PPR-NAT', 'SUP-KH', 'RIM', 500, 7],
  ['ETK-BOLD20', 'SUP-BMJ', 'BOX', 33000, 8],
  ['OPP-125', 'SUP-BMJ', 'ROLL', 3000, 10],
  ['FOIL-BLK86', 'SUP-BMJ', 'ROLL', 2000, 12],
  ['FOIL-GLD70', 'SUP-BMJ', 'ROLL', 2000, 12],
  ['PPR-REG', 'SUP-BMJ', 'ROLL', 6000, 10],
  ['PPR-MILD', 'SUP-BMJ', 'ROLL', 6000, 10],
  ['CTP-BOLD', 'SUP-BMJ', 'ROLL', 3000, 9],
  ['ETK-BOLD12', 'SUP-IJ', 'BOX', 25000, 14],
  ['ETK-XTRA20', 'SUP-IJ', 'BOX', 25000, 11],
  ['TT-GLDH', 'SUP-IJ', 'ROLL', 16000, 8],
  ['OPP-PRS300', 'SUP-IJ', 'BOX', 2000, 9],
  ['KRF-75', 'SUP-IJ', 'BUNDLE', 100, 4],
  ['PPR-NAT', 'SUP-IJ', 'PAK', 100, 14],
  ['FLT-B06', 'SUP-FLI', 'DOS', 4000, 5],
  ['FLT-SF13', 'SUP-FLI', 'DOS', 4000, 5],
  ['LEM-C', 'SUP-KTM', 'PAIL', 25, 9],
  ['TSG-REG', 'SUP-TMG', 'SAK', 30, 7],
  ['TSG-MLD', 'SUP-MDR', 'SAK', 30, 12]
]

/**
 * Saldo In Stock prototype (unit besar) → satu rm_unit per material (stok awal 1 Agu).
 * Tembakau: TSG-REG 97 Sak & 40 dari 58 Sak TSG-MLD berasal dari unit penerimaan tembakau
 * (`wms-inbound.seed.ts`), sehingga di sini hanya sisa stok awalnya — total saldo tetap sama.
 */
const STOCK_IN: [string, number][] = [
  ['TSG-MLD', 18],
  ['ETK-BOLD20', 14],
  ['FLT-B06', 15],
  ['PPR-REG', 20],
  ['ETK-XTRA20', 10]
]

const MACHINES = [
  ['MOLLINS-1', 'Mollins 1'],
  ['MOLLINS-2', 'Mollins 2'],
  ['PROTOS-1', 'Protos 1'],
  ['PROTOS-2', 'Protos 2'],
  ['SASIB', 'SASIB'],
  ['PRO-MILD', 'Pro Mild']
] as const

const indexOf = <T extends readonly (readonly unknown[])[]>(list: T, code: string) =>
  list.findIndex(item => item[0] === code) + 1

export const materialId = (code: string) => seedId(SEED_GROUP.material, MATERIALS.findIndex(([c]) => c === code) + 1)
export const unitId = (code: UnitCode) => seedId(SEED_GROUP.unit, indexOf(UNITS, code))
export const supplierId = (code: SupplierCode) => seedId(SEED_GROUP.company, indexOf(SUPPLIERS, code))
export const machineId = (code: (typeof MACHINES)[number][0]) => seedId(SEED_GROUP.machine, indexOf(MACHINES, code))
export const warehouseRmId = seedId(SEED_GROUP.warehouse, 1)
export const categoryId = (code: CategoryCode) => seedId(SEED_GROUP.materialGroup, indexOf(CATEGORIES, code))

/** Isi unit kecil per unit besar default (mis. 30 kg per Sak). */
export const smallPerBigOf = (materialCode: string) => MATERIALS.find(([code]) => code === materialCode)?.[4] ?? 1

export type { SupplierCode, UnitCode }

/** Unit besar default (unit beli) suatu material — dipakai PR line `unit_id`. */
export const purchaseUnitOf = (materialCode: string) => {
  const material = MATERIALS.find(([code]) => code === materialCode)
  return material ? unitId(material[3]) : null
}

export function buildWmsMasterSeed(): SeedTables {
  const materials: LocalRow[] = MATERIALS.map(
    ([code, name, category, unitBig, perBig, unitSmall, capDay, variant]) => ({
      ...seedAudit,
      id: materialId(code),
      code,
      name,
      category_id: seedId(SEED_GROUP.materialGroup, indexOf(CATEGORIES, category)),
      unit_small_id: unitId(unitSmall),
      cap_day: capDay,
      rop_manual: null,
      variant,
      default_purchase_unit_id: unitId(unitBig),
      default_purchase_unit_qty: perBig,
      is_active: true
    })
  )

  return {
    mt_material_group: CATEGORIES.map(([code, name], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.materialGroup, index + 1),
      code: code.toUpperCase(),
      name,
      is_active: true
    })),
    mt_unit: UNITS.map(([code, name, kind], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.unit, index + 1),
      code,
      name,
      kind,
      is_active: true
    })),
    mt_material: materials,
    mt_companies: [
      ...SUPPLIERS.map(([code, name, city], index) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.company, index + 1),
        code,
        name,
        type: 'supplier',
        city,
        address: null,
        phone: null,
        is_active: true
      })),
      ...CUSTOMERS.map(([code, name, city, address], index) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.company, 100 + index + 1),
        code,
        name,
        type: 'customer',
        city,
        address,
        phone: null,
        is_active: true
      }))
    ],
    mt_material_pack_standard: PACKS.map(([material, supplier, unitBig, perBig, leadDays], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.packStandard, index + 1),
      material_id: materialId(material),
      supplier_id: supplierId(supplier),
      unit_big_id: unitId(unitBig),
      small_per_big: perBig,
      lead_days: leadDays
    })),
    mt_warehouses: [
      { ...seedAudit, id: warehouseRmId, code: 'WH-RM', name: 'Gudang Raw Material', kind: 'RM', is_active: true },
      {
        ...seedAudit,
        id: seedId(SEED_GROUP.warehouse, 2),
        code: 'WH-FG',
        name: 'Gudang Finish Good',
        kind: 'FG',
        is_active: true
      }
    ],
    mt_equipment: MACHINES.map(([code, name], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.machine, index + 1),
      code,
      name,
      workcenter_id: null,
      is_active: true
    })),
    rm_unit: STOCK_IN.map(([material, qtyBig], index) => {
      const row = MATERIALS.find(([code]) => code === material)
      return {
        ...seedAudit,
        id: seedId(SEED_GROUP.rmUnit, index + 1),
        uid: `UNIT-${String(index + 1).padStart(6, '0')}`,
        qr_code: `${material}:20260801:01:${String(index + 1).padStart(4, '0')}`,
        material_id: materialId(material),
        qty_small: qtyBig * (row?.[4] ?? 1),
        flag: 'REGULAR',
        unit_status: 'IN_STOCK',
        is_leftover_wip: false
      }
    })
  }
}
